import { create } from 'zustand';

import type BuilderComponent from 'components/core/createModify/builder/Builder';
import type Step from 'components/core/createModify/interfaces/step';
import type Tier from 'components/core/createModify/interfaces/tier';
import type { BuilderEntry } from 'components/sections/createModify/interfaces';
import type { CreateModifyContextInterface, ToggleTierData } from 'contexts/CreateModifyContext';
import type { BuilderConfig } from 'enums/builderType';
import type { FeatureFlagSet } from 'hooks/useFeatureFlags';

import { searchStepForActiveField } from 'components/core/createModify/helpers/createModifyHelpers';
import { isFieldsResolver } from 'components/core/createModify/helpers/stepResolvers';
import { GTMEvents } from 'components/core/logging/GTMLogger';
import LoggingService from 'components/core/logging/LoggingService';
import { CreateModifyTiers } from 'enums/createModifyTiers';
import { importWithStaleChunkRecovery, isChunkLoadError } from 'utils/chunkRecoveryUtils';
import { isLocallyServed } from 'utils/runtimeUtils';

export type BuilderPreloadReason = 'focus' | 'pointerdown' | 'pointerenter';

export type BuilderRegistry =
  | {
      /**
       * No builder has been requested yet, or the last download failed and the next request retries it.
       */
      status: 'idle';
    }
  | {
      /**
       * The builder UI, downloaded with the configuration so rendering it never suspends.
       */
      Builder: typeof BuilderComponent;
      /**
       * Every builder's configuration, downloaded on the first builder request.
       */
      config: BuilderConfig;
      /**
       * The intent that started this download.
       */
      startedBy: BuilderLoadReason;
      /**
       * Builder code has downloaded, so tiers open synchronously.
       */
      status: 'loaded';
    }
  | {
      /**
       * The latest request for each tier made while builder code downloads, opened when it arrives.
       */
      pending: PendingTiers;
      /**
       * The intent that started this download.
       */
      startedBy: BuilderLoadReason;
      /**
       * Builder code is downloading.
       */
      status: 'loading';
    };

type BuilderLoadReason = 'open' | BuilderPreloadReason;

type BuilderShellWait = {
  /**
   * Whether the request starts a download, joins one, or uses downloaded code.
   */
  availability: 'cached' | 'cold' | 'joined';
  /**
   * The builder type already supplied by the requesting open path.
   */
  builderType?: ToggleTierData['type'];
  /**
   * Monotonic start time in milliseconds.
   */
  startedAt: number;
  /**
   * The intent that started the code download.
   */
  startedBy: BuilderLoadReason;
};

type BuilderShellWaits = Partial<Record<CreateModifyTiers, BuilderShellWait[]>>;

type LoadedBuilders = Omit<Extract<BuilderRegistry, { status: 'loaded' }>, 'startedBy' | 'status'>;

type PendingTiers = Partial<Record<CreateModifyTiers, ToggleTierData>>;

const shellAvailability = {
  idle: 'cold',
  loaded: 'cached',
  loading: 'joined',
} satisfies Record<BuilderRegistry['status'], BuilderShellWait['availability']>;

const loadBuilders = async (shouldReload: () => boolean): Promise<LoadedBuilders> => {
  const [{ Builders }, { default: Builder }] = await importWithStaleChunkRecovery(
    () =>
      Promise.all([
        import(/* webpackChunkName: "builder-registry" */ 'components/sections/createModify/builders'),
        import(/* webpackChunkName: "builder-registry" */ 'components/core/createModify/builder/Builder'),
      ]),
    shouldReload
  );

  return { Builder, config: Builders };
};

/**
 * The create/modify store's stable action/helper bundle — a stable reference, so subscribing to it
 * (via `useCreateModifyActions`) never triggers a re-render. Holds the public tier actions plus the
 * dependency-injection setters the provider calls to feed React-sourced values (featureFlags, the
 * load-failure notice) into the store.
 */
export type CreateModifyActions = {
  /**
   * Completes the measured requests observed by a committed builder container.
   */
  completeShellWait: (tierId: CreateModifyTiers, waits: BuilderShellWait[]) => void;
  /**
   * Returns the field configuration for a field within a step (pure; no resolver-based fields).
   */
  getField: CreateModifyContextInterface['getField'];
  /**
   * Returns the latest tier data for a tier ID, read synchronously via `get()`.
   */
  getTier: CreateModifyContextInterface['getTier'];
  /**
   * Downloads builder code without opening a tier or showing pending UI.
   */
  preloadBuilders: (reason: BuilderPreloadReason) => void;
  /**
   * Inject the LaunchDarkly feature flags — called by `CreateModifyProvider` in a `useLayoutEffect`.
   * Sourced from `useFeatureFlags()` (the same source as today); needed to filter flag-gated steps.
   * Also re-derives `topTier` so open tiers pick up the new flags immediately. Note: re-filtering can
   * only hide steps that are currently visible — steps already filtered out cannot be restored because
   * the original (pre-filter) step array is not retained.
   */
  setFeatureFlags: (featureFlags: FeatureFlagSet) => void;
  /**
   * Inject what tells the user a builder could not open because its code failed to download — called
   * by `CreateModifyProvider`, which owns notifications.
   */
  setOnBuilderLoadFailure: (onBuilderLoadFailure: () => void) => void;
  /**
   * Updates an existing tier with partial data, reading prior state synchronously via `get()`.
   */
  setTier: CreateModifyContextInterface['setTier'];
  /**
   * Instantiates or removes a tier, reading prior state synchronously via `get()`.
   */
  toggleTier: CreateModifyContextInterface['toggleTier'];
};

interface CreateModifyStoreState {
  /**
   * LaunchDarkly feature flags — injected by `CreateModifyProvider` via `setFeatureFlags`.
   */
  _featureFlags: FeatureFlagSet;
  _onBuilderLoadFailure: () => void;
  /**
   * Static action/helper bundle; consumed via `useCreateModifyActions`, never off the raw store.
   */
  actions: CreateModifyActions;
  registry: BuilderRegistry;
  /**
   * Measured user requests awaiting their first committed shell.
   */
  shellWaits: BuilderShellWaits;
  /**
   * Map of all current tiers by their tier ID.
   */
  tiers: Record<string, Tier | undefined>;
  /**
   * The top-most (currently active) tier, with feature-flag filtering applied.
   */
  topTier: Tier | undefined;
}

/**
 * Pure: returns a copy of `tier` with feature-flag-gated steps removed. Replaces the legacy
 * `featureFlagSteps` callback, which reassigned `tier.steps` in place — fine against a local ref,
 * but state-corrupting in a shared store. A step survives when it has no `featureFlagsRequired`, or
 * when every required flag is truthy in `featureFlags`.
 */
const filterTierSteps = (tier: Tier, featureFlags: FeatureFlagSet): Tier => ({
  ...tier,
  steps: tier.steps?.filter(
    step => !step.featureFlagsRequired?.length || step.featureFlagsRequired.every(flag => !!featureFlags[flag])
  ),
});

/**
 * Finds the Step containing `activeField` within a builder. For steps with resolver-based fields,
 * `tier` enables field resolution. `searchStepForActiveField` may set `tier.isExpanded` based on the
 * field's `shouldExpandBuilderOnEdit` config — safe here because the caller (`toggleTier`) passes a
 * freshly-constructed tier object. Falls back to matching by step id.
 */
const findActiveStep = (activeField: string, builder: BuilderEntry, tier?: Tier): Step<any, any, any> | undefined => {
  if (!builder?.steps) {
    return;
  }

  for (const step of builder.steps) {
    const foundStep = searchStepForActiveField(step, activeField, tier);
    if (foundStep) {
      return foundStep;
    }
  }

  return builder.steps.find(step => step.id === activeField);
};

const openTier = (config: BuilderConfig, tierId: CreateModifyTiers, tierData: ToggleTierData): Tier => {
  const { activeStepId, mode, ...rest } = tierData;

  const tier = { data: {}, metadata: {}, ...rest, mode, tierId } satisfies Tier;
  const builder = tier.type ? config[tier.type] : undefined;

  if (activeStepId && builder) {
    tier['activeStep'] = builder.steps?.find(step => step.id === activeStepId);

    if (process.env.NODE_ENV === 'development' && !tier['activeStep']) {
      LoggingService.warn({
        message: `activeStepId '${activeStepId}' not found in builder '${tier.type}'`,
        messageContext: { activeStepId, availableSteps: builder.steps?.map(s => s.id), builderType: tier.type },
      });
    }
  } else if (builder && tier.activeField) {
    tier['activeStep'] = findActiveStep(tier.activeField, builder, tier);
  }

  return tier;
};

/**
 * Recomputes the derived `topTier` for `nextTiers` and returns the `{ tiers, topTier }` slice to
 * `set()`. Replaces the provider's `updateTiers`/`setTopTier`. Two behaviours preserved from the
 * legacy provider, but immutably:
 * - The top tier's steps are feature-flag-filtered and the filtered copy is written back into the map,
 *   so `tiers[topTierId]` and `topTier` share the same filtered object (the legacy in-place mutation
 *   produced this).
 * - When the top tier object itself is unchanged (e.g. a non-top tier was edited), the existing
 *   derived `topTier` reference is kept so `useTopTier` subscribers do not re-render — the selector
 *   isolation the legacy provider got for free from React's Object.is state bail-out.
 */
const deriveTierState = (
  nextTiers: Record<string, Tier | undefined>,
  prevTiers: Record<string, Tier | undefined>,
  prevTopTier: Tier | undefined,
  featureFlags: FeatureFlagSet
): Pick<CreateModifyStoreState, 'tiers' | 'topTier'> => {
  const topTierId = Object.values(CreateModifyTiers).findLast(tierId => !!nextTiers[tierId]);
  const source = topTierId ? nextTiers[topTierId] : undefined;

  if (!topTierId || !source) {
    return { tiers: nextTiers, topTier: undefined };
  }

  if (prevTopTier?.tierId === topTierId && source === prevTiers[topTierId]) {
    return { tiers: nextTiers, topTier: prevTopTier };
  }

  const filteredTop = filterTierSteps(source, featureFlags);

  return { tiers: { ...nextTiers, [topTierId]: filteredTop }, topTier: filteredTop };
};

/**
 * Single source of truth for builder tier state (`tiers`, `topTier`). A Zustand store (not Context)
 * so tier actions can read the latest committed state synchronously via `get()` — eliminating the
 * `useRef(tiers)` workaround that the old React `useState` batching forced. `subContexts` stays on
 * React context (owned by `CreateModifyProvider`; ED-19164 owns its removal). Modeled on
 * `useUserStore` (ED-19161).
 *
 * Module-private by design — consumers (and tests) go through the atomic hooks, the
 * `useCreateModifyActions` bundle, or the `seedCreateModifyStore`/`resetCreateModifyStore` seam,
 * never the raw store (Zustand convention — see tkdodo.eu/blog/working-with-zustand).
 */
const useCreateModifyStore = create<CreateModifyStoreState>((set, get) => {
  const finishShellWaits = (
    tierId: CreateModifyTiers,
    outcome: 'cancel' | 'failure' | 'success',
    waits = get().shellWaits[tierId] ?? []
  ) => {
    const { shellWaits } = get();
    const currentWaits = shellWaits[tierId] ?? [];
    const completed = currentWaits.filter(wait => waits.includes(wait));
    if (completed.length === 0) {
      return;
    }
    const { [tierId]: _completed, ...remainingTiers } = shellWaits;
    const remaining = currentWaits.filter(wait => !waits.includes(wait));
    set({ shellWaits: remaining.length ? { ...remainingTiers, [tierId]: remaining } : remainingTiers });
    for (const { startedAt, ...context } of completed) {
      LoggingService.trackAction(GTMEvents.BUILDER_SHELL_WAIT, {
        ...context,
        durationMs: performance.now() - startedAt,
        outcome,
      });
    }
  };

  const completeLoad = ({ Builder, config }: LoadedBuilders) => {
    const { _featureFlags, registry, tiers, topTier } = get();

    if (registry.status !== 'loading') {
      return;
    }

    set({ registry: { Builder, config, startedBy: registry.startedBy, status: 'loaded' } });

    const nextTiers = { ...tiers };
    for (const tierId of Object.values(CreateModifyTiers)) {
      const tierData = registry.pending[tierId];
      if (tierData) {
        nextTiers[tierId] = { ...openTier(config, tierId, tierData), openedFromPending: true };
      }
    }

    set(deriveTierState(nextTiers, tiers, topTier, _featureFlags));
  };

  const failOpen = (error: Error) => {
    for (const tierId of Object.values(CreateModifyTiers)) {
      finishShellWaits(tierId, 'failure');
    }
    LoggingService.error({ error, message: 'Builder failed to open', messageContext: { error } });
  };

  const failLoad = (error: Error) => {
    const { _onBuilderLoadFailure, registry } = get();

    if (registry.status !== 'loading') {
      return;
    }

    for (const tierId of Object.values(CreateModifyTiers)) {
      finishShellWaits(tierId, 'failure');
    }
    set({ registry: { status: 'idle' } });

    if (Object.keys(registry.pending).length === 0) {
      return;
    }

    if (!isChunkLoadError(error)) {
      failOpen(error);

      return;
    }

    LoggingService.error({ error, message: 'Builder registry failed to download', messageContext: { error } });

    if (Object.keys(registry.pending).length > 0) {
      _onBuilderLoadFailure();
    }
  };

  const requestTier = (
    registry: Exclude<BuilderRegistry, { status: 'loaded' }>,
    tierId: CreateModifyTiers,
    tierData?: ToggleTierData
  ) => {
    const { [tierId]: _replaced, ...pending } = registry.status === 'loading' ? registry.pending : {};

    if (registry.status === 'loading') {
      set({
        registry: { ...registry, pending: tierData ? { ...pending, [tierId]: tierData } : pending, status: 'loading' },
      });

      return;
    }

    if (!tierData) {
      return;
    }

    startLoad({ [tierId]: tierData }, 'open');
  };

  const startLoad = (pending: PendingTiers, startedBy: BuilderLoadReason) => {
    set({ registry: { pending, startedBy, status: 'loading' } });
    void loadBuilders(() => {
      const { registry } = get();

      return registry.status === 'loading' && Object.keys(registry.pending).length > 0;
    })
      .catch(failLoad)
      .then(loaded => {
        if (loaded) {
          completeLoad(loaded);
        }
      })
      .catch(failOpen);
  };

  return {
    _featureFlags: {} as FeatureFlagSet,
    _onBuilderLoadFailure: () => {},
    actions: {
      completeShellWait: (tierId, waits) => finishShellWaits(tierId, 'success', waits),

      getField: (step, field) => {
        if (!step?.fields || isFieldsResolver(step.fields)) {
          return;
        }
        const fields = step.fields;
        const fieldKey = Object.keys(fields).find(
          key => key === field || fields?.[key]?.queryAlias?.includes(field) || fields?.[key]?.queryAlias === field
        );

        return fieldKey ? { queryVar: fieldKey, ...fields[fieldKey] } : undefined;
      },

      getTier: tierId => get().tiers[tierId],

      preloadBuilders: reason => {
        if (get().registry.status === 'idle') {
          startLoad({}, reason);
        }
      },

      setFeatureFlags: featureFlags => {
        const { tiers } = get();
        // Pass undefined as prevTopTier to force re-filtering — the old topTier was filtered with stale flags.
        set({ _featureFlags: featureFlags, ...deriveTierState(tiers, tiers, undefined, featureFlags) });
      },
      setOnBuilderLoadFailure: onBuilderLoadFailure => set({ _onBuilderLoadFailure: onBuilderLoadFailure }),

      setTier: (tierId, tierUpdates) => {
        const { _featureFlags, tiers, topTier } = get();
        const currentTier = tiers[tierId];

        if (!currentTier) {
          LoggingService.debug({ message: `tier: ${tierId} does not exist, no updates called.` });

          return;
        }

        /*
         * Bump errorRevision to signal StepErrors to re-show a dismissed overlay (see Tier.errorRevision).
         * Treat any fresh, non-empty errors set as an assertion worth re-showing — a failed save from the
         * centralized flow (which clears to [] first) OR a step that populates errors directly without
         * clearing (FiltersStep, MediaStep, etc.). The only path that must NOT bump is clearFieldError's
         * incremental shrink on keystroke, which always yields a shorter list, hence the length guard.
         * The `!==` guard skips StepComponentCore's intentional duplicate setTier call, which passes the
         * same array reference twice and would otherwise double-bump.
         */
        const nextErrors = tierUpdates.errors;
        const errorsAsserted =
          'errors' in tierUpdates &&
          !!nextErrors?.length &&
          nextErrors !== currentTier.errors &&
          nextErrors.length >= (currentTier.errors?.length ?? 0);

        const updatedTier: Tier = {
          ...currentTier,
          ...tierUpdates,
          errorRevision: errorsAsserted ? (currentTier.errorRevision ?? 0) + 1 : currentTier.errorRevision,
          /*
           * Merge objects while disallowing required keys to be overwritten if explicitly 'undefined' in tierUpdates.
           * For `data`, we merge with currentTier.data to preserve critical fields like `id` that may not be
           * included in partial updates - this prevents mutations from failing due to missing ID.
           */
          data: tierUpdates.data
            ? {
                ...currentTier.data,
                ...tierUpdates.data,
                // Prevent id from being cleared by partial updates (ED-17285)
                ...(currentTier.data?.id && !tierUpdates.data?.id && { id: currentTier.data.id }),
              }
            : currentTier.data,
          metadata: tierUpdates.metadata ? { ...currentTier.metadata, ...tierUpdates.metadata } : currentTier.metadata,
          tierId: tierUpdates.tierId || currentTier.tierId,
          type: tierUpdates.type || currentTier.type,
        };

        set(deriveTierState({ ...tiers, [tierId]: updatedTier }, tiers, topTier, _featureFlags));
      },

      toggleTier: (tierId, tierData) => {
        const { _featureFlags, registry, tiers, topTier } = get();

        if (tierData && !tiers[tierId]) {
          const wait: BuilderShellWait = {
            availability: shellAvailability[registry.status],
            ...(tierData.type && { builderType: tierData.type }),
            startedAt: performance.now(),
            startedBy: registry.status === 'idle' ? 'open' : registry.startedBy,
          };
          const { shellWaits } = get();
          set({ shellWaits: { ...shellWaits, [tierId]: [...(shellWaits[tierId] ?? []), wait] } });
        } else if (!tierData) {
          finishShellWaits(tierId, 'cancel');
        }

        if (isLocallyServed()) {
          LoggingService.log({
            message: `Toggle tier: ${tierId}`,
            messageContext: { currentTiers: tiers, tierData },
          });
        }

        if (registry.status !== 'loaded') {
          requestTier(registry, tierId, tierData);

          return;
        }

        const nextTiers = { ...tiers };

        if (tierData) {
          try {
            nextTiers[tierId] = openTier(registry.config, tierId, tierData);
          } catch (error) {
            finishShellWaits(tierId, 'failure');
            throw error;
          }
        } else {
          delete nextTiers[tierId];
        }

        set(deriveTierState(nextTiers, tiers, topTier, _featureFlags));
      },
    },
    registry: { status: 'idle' },
    shellWaits: {},
    tiers: {},
    topTier: undefined,
  };
});

/**
 * Map of all current tiers by their tier ID.
 */
export const useTiers = () => useCreateModifyStore(s => s.tiers);

/**
 * The top-most (currently active) tier, with feature-flag filtering applied.
 */
export const useTopTier = () => useCreateModifyStore(s => s.topTier);

/**
 * The builder registry's download state, for showing that a requested builder is loading.
 */
export const useBuilderRegistry = () => useCreateModifyStore(s => s.registry);

/**
 * Requests awaiting their first committed shell, including repeated requests for the same pending tier.
 */
export const useBuilderShellWaits = () => useCreateModifyStore(s => s.shellWaits);

/**
 * The create/modify store's stable action/helper bundle. Stable reference — never re-renders on its own.
 */
export const useCreateModifyActions = () => useCreateModifyStore(s => s.actions);

const _INITIAL_STATE = useCreateModifyStore.getState();

/**
 * Resets the Zustand create/modify store to its initial empty state, including all action references.
 * Call this in test `beforeEach` (or a test/story wrapper) to prevent state leaking between tests.
 */
export const resetCreateModifyStore = () => useCreateModifyStore.setState(_INITIAL_STATE, true);

/**
 * Shape accepted by {@link seedCreateModifyStore}: any data field plus a partial `actions` override.
 */
type CreateModifyStoreSeed = Partial<Omit<CreateModifyStoreState, 'actions'>> & {
  /**
   * Action overrides (e.g. `jest.fn()`s) merged onto the live bundle; omitted actions are preserved.
   */
  actions?: Partial<CreateModifyActions>;
};

/**
 * Seeds the create/modify store for tests and Storybook. Data fields shallow-merge; the `actions`
 * partial is merged onto the existing bundle, so callers can override only the actions they care
 * about without dropping the rest. Seed `registry`/`_featureFlags` here to control step
 * resolution and flag filtering in store unit tests.
 *
 * @param partial - Partial store state to merge in. `partial.actions` is merged into the live bundle.
 */
export const seedCreateModifyStore = (partial: CreateModifyStoreSeed) =>
  useCreateModifyStore.setState(state => ({
    ...partial,
    actions: partial.actions ? { ...state.actions, ...partial.actions } : state.actions,
  }));
